import { useState } from "react"

import type { ServiceConnectionData, ServiceConnectionService, UIEvent, UIResponse } from "../types"
import { actionLabel, CardActions, CardButton, CardFallback, CardSettledAction, CardShell } from "./card_shell"
import { connectService } from "./service_connect"
import styles from "./service_connection_card.module.scss"

interface Props {
  ui: UIEvent
  response?: UIResponse
  onRespond: (uiId: string, actionId: string, value: unknown, displayText?: string) => void
}

function brandStyle(service: ServiceConnectionService) {
  return service.brand_color ? { backgroundColor: `#${service.brand_color}` } : undefined
}

const ServiceConnectionCard = ({ ui, onRespond, response }: Props) => {
  const [answered, setAnswered] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [failed, setFailed] = useState(false)

  const { prompt, service } = ui.data as ServiceConnectionData
  const url = service?.mode === "reconnect" ? service.reconnect_url : service?.connect_url

  if (!service || (!url && !response)) {
    return <CardFallback text={ui.fallback_text || `(${ui.type})`} />
  }

  const details = (
    <div className={styles["body"]}>
      <span className={styles["icon"]} style={brandStyle(service)}>
        {service.icon_url ? <img src={service.icon_url} alt={service.label} width="28" height="28" /> : null}
      </span>
      <div className={styles["details"]}>
        <span className={styles["name"]}>{service.label}</span>
        {service.description ? <p className={styles["description"]}>{service.description}</p> : null}
        <a className={styles["learn-more"]} href={`/${service.value}`} target="_blank" rel="noopener noreferrer">
          Learn more about {service.label}
        </a>
      </div>
    </div>
  )

  if (response) {
    return (
      <CardShell>
        {details}
        <CardSettledAction label={response.displayText ?? service.label} confirmed={response.actionId === "select"} />
      </CardShell>
    )
  }

  const busy = answered || connecting

  const dismiss = () => {
    if (busy) return
    setAnswered(true)
    onRespond(ui.id, "dismiss", undefined, actionLabel(ui.actions, "dismiss", "Not right now"))
  }

  const connect = () => {
    if (busy) return
    setConnecting(true)
    setFailed(false)
    void connectService(service).then(connected => {
      if (connected) {
        setAnswered(true)
        onRespond(ui.id, "select", undefined, `Connected ${service.label}`)
      } else {
        setConnecting(false)
        setFailed(true)
      }
    })
  }

  return (
    <CardShell prompt={prompt} promptSize="lg" onDismiss={() => onRespond(ui.id, "close", undefined)}>
      {details}
      {failed ? (
        <p className={styles["error"]} role="alert">
          Couldn{"’"}t connect {service.label}. Please try again.
        </p>
      ) : null}
      <CardActions>
        <CardButton variant="secondary" disabled={busy} onClick={dismiss}>
          Not right now
        </CardButton>
        <CardButton variant="primary" disabled={busy} onClick={connect}>
          {connecting ? "Connecting…" : failed ? "Try again" : "Connect"}
        </CardButton>
      </CardActions>
    </CardShell>
  )
}

export default ServiceConnectionCard
